import Choices from 'choices.js';
import { rememberFkDisplay } from './fk_display_cache';
//import 'choices.js/public/assets/styles/choices.min.css';

// Simple debounce function
function debounce(func, delay) {
  let timeout;
  return function(...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
}

export class ForeignKeyEditor {
  
    getInitialChoice(key, params) {
    if (key === null || key === undefined || String(key).trim() === '') {
      return null;
    }

    let pkDisplay = '';
    let nameDisplay = '';
    const fkColId = params.colDef.colId;

    // 1. The referenced business key, supplied by the server as one
    //    `<col>_<pkName>_ref` field per key column on the row.
    const pkColumnNames = params.colDef.cellEditorParams?.foreignKeyReferenceDetails
                            ?.primary_key_column_names || [];
    if (params.data) {
      const validPkValues = pkColumnNames
        .map(pkName => params.data[`${fkColId}_${pkName}_ref`])
        .filter(val => val !== null && val !== undefined && String(val).trim() !== '')
        .map(String);
      if (validPkValues.length > 0) {
        pkDisplay = validPkValues.join(', ');
      }
    }

    // 2. Get pre-fetched display name from row data (params.data)
    const displayNameColName = `${fkColId}_name_ref`;
    if (params.data && params.data.hasOwnProperty(displayNameColName)) {
      const fetchedName = params.data[displayNameColName];
      if (fetchedName !== null && fetchedName !== undefined && String(fetchedName).trim() !== '') {
        nameDisplay = String(fetchedName);
      }
    }

    // 3. Construct the final label
    let finalLabel = '';
    const preFetchedDisplayNamePart = nameDisplay || '';
    const keysPart = pkDisplay ? `(${pkDisplay})` : '';

    if (preFetchedDisplayNamePart && keysPart) {
      finalLabel = `${preFetchedDisplayNamePart} ${keysPart}`;
    } else if (preFetchedDisplayNamePart) {
      finalLabel = preFetchedDisplayNamePart;
    } else if (keysPart) {
      finalLabel = keysPart;
    } else {
      finalLabel = 'Current Selection'; // Fallback if no parts, but a value exists
    }

    return { value: key, label: finalLabel, selected: true };
  }

  init(params) {
    this.params = params;
    this.fkDetails = params.colDef.cellEditorParams.foreignKeyReferenceDetails;
    // params.value is the referenced record's trdm_record_id.
    this.selectedValue = this.getInitialChoice(params.value, params);
    this.selectedKey = this.selectedValue?.value || '';
    this.initialChoiceObject = null // To store the {value, label} of the initial selection
    // Guards for in-flight searches: see isStaleSearch.
    this.searchSeq = 0;
    this.destroyed = false;
    this.committed = false;

    if (!this.fkDetails || !this.fkDetails.foreign_table_id) {
      console.error('ForeignKeyEditor: Missing foreignKeyReferenceDetails or foreign_table_id', params);
      // Create a non-functional display to indicate error
      this.eGui = document.createElement('div');
      // Tailwind classes for error display
      this.eGui.classList.add('custom-choices-editor-wrapper', 'p-4', 'border', 'border-red-400', 'bg-red-100', 'text-red-700', 'rounded-md');
      this.eGui.textContent = 'Error: Editor misconfigured';
      return;
    }

    this.eGui = document.createElement('div');
    // AG Grid will add 'ag-popup-editor'. We add our custom class and Tailwind classes.
     this.eGui.classList.add(
       'custom-choices-editor-wrapper',
       'bg-white',
       'border',
       'border-gray-300',
       'shadow-lg',
       'rounded-md',
       'p-4', // Padding
       'min-w-[350px]' // Tailwind JIT for min-width. Adjust as needed, e.g., 'min-w-md'
     );

    // Clear link — only visible when the cell has an existing value
    this.clearLink = document.createElement('a');
    this.clearLink.textContent = 'Clear value';
    this.clearLink.href = '#';
    this.clearLink.classList.add('text-xs', 'text-red-500', 'hover:text-red-700', 'cursor-pointer', 'block', 'ml-auto');
    // The cell value is the referenced record id, a number, so this must not assume a string.
    if (this.params.value === null || this.params.value === undefined || String(this.params.value).trim() === '') {
      this.clearLink.classList.add('hidden');
    }
    this.clearLink.addEventListener('click', (e) => {
      e.preventDefault();
      this.selectedValue = null;
      this.selectedDisplayName = null;
      this.selectedKeyParts = [];
      this.committed = true;
      this.params.api.stopEditing();
    });
    // Add flex container to right-align the clear link
    const headerContainer = document.createElement('div');
    headerContainer.classList.add('flex', 'justify-between', 'items-center', 'mb-2');
    headerContainer.appendChild(this.clearLink);
    this.eGui.appendChild(headerContainer);

    this.selectElement = document.createElement('select');
    this.eGui.appendChild(this.selectElement);

    this.choicesInstance = new Choices(this.selectElement, {
      searchEnabled: true,
      shouldSort: false,
      allowHTML: false,
      loadingText: 'Loading...',
      noResultsText: 'No results found',
      noChoicesText: 'No choices to choose from',
      itemSelectText: 'Click to select',
      searchPlaceholderValue: 'Search...',
      placeholder: true,
      placeholderValue: 'Select an option'
    });

    this.selectElement.addEventListener('change', () => {
      this.selectedValue = this.choicesInstance.getValue(true); // Choices hands back a string
      // Extract display_name from the selected choice's customProperties
      const selectedChoice = this.choicesInstance.getValue();
      if (selectedChoice && selectedChoice.customProperties && selectedChoice.customProperties.display_name !== undefined) {
        this.selectedDisplayName = selectedChoice.customProperties.display_name;
        this.selectedKeyParts = selectedChoice.customProperties.key_parts || [];
      } else {
        this.selectedDisplayName = null;
        this.selectedKeyParts = [];
      }
      // Stop editing and apply the new value. This will trigger the update process in ag_grid_controller.
      this.committed = true;
      this.params.api.stopEditing();
    });

    // Debounce the search function
    this.debouncedSearch = debounce(this.performSearch, 300).bind(this);
    this.choicesInstance.input.element.addEventListener('input', (event) => {
        // Only trigger search if the input element is part of choices (i.e., not a programmatic change)
        if (event.target === this.choicesInstance.input.element) {
            this.debouncedSearch(event.target.value);
        }
    });

    // Handle Escape key to cancel editing
    this.eGui.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.params.api.stopEditing(true); // true to cancel
      }
    });
  }

  // A search resolves asynchronously, so by the time it returns the editor may be gone, a
  // newer search may have superseded it, or the user may already have picked a value. In
  // any of those cases it must not touch the widget: setChoices(..., true) REPLACES the
  // list and drops the current selection, so a late response can blank the value just
  // chosen - which is why a selection sometimes failed to appear in the cell - and once
  // destroy() has nulled choicesInstance it throws outright.
  isStaleSearch(seq) {
    return this.destroyed || this.committed || !this.choicesInstance || seq !== this.searchSeq;
  }

  async performSearch(searchTerm) {
    if (!this.fkDetails || !this.fkDetails.foreign_table_id) return;
    if (this.destroyed || this.committed) return;

    const seq = ++this.searchSeq;
    const referencedTableId = this.fkDetails.foreign_table_id;
    const url = `/api/tables/${referencedTableId}/referenced_data?searchTerm=${encodeURIComponent(searchTerm)}`;

    try {
      const response = await fetch(url);
      if (this.isStaleSearch(seq)) return;
      if (!response.ok) {
        console.error('ForeignKeyEditor: Error fetching referenced data', response.statusText);
        this.choicesInstance.setChoices([], 'value', 'label', true); // Clear choices on error
        return;
      }
      const apiData = await response.json();
      if (this.isStaleSearch(seq)) return;

      // Record how each option displays. The grid controller reads this when applying the
      // edit, which is the only place that reliably has the live row.
      const fkColId = this.params.colDef.colId;
      apiData.forEach(o => rememberFkDisplay(fkColId, o.value, o.customProperties));
      
      let choicesToSet = apiData;

      // Ensure the initially selected item (if any) remains available if it's not in the search results
      // This is primarily for the case where the user clears the search after having an initial value
      if (this.initialChoiceObject && this.initialChoiceObject.value) {
          const initialValueStillInResults = apiData.some(choice => choice.value === this.initialChoiceObject.value);
          if (!initialValueStillInResults) {
              // Prepend the initial choice if it's not part of the current search results
              // This ensures it's always in the list if it was the original value of the cell
              choicesToSet = [this.initialChoiceObject, ...apiData].filter((item, index, self) => 
                index === self.findIndex((t) => t.value === item.value) // Keep unique by value
              );
          }
      }
      this.choicesInstance.setChoices(choicesToSet, 'value', 'label', true); // true to replace current choices

      // Re-apply the current selection if it exists, as setChoices might clear it
      if (this.selectedValue) {
        this.choicesInstance.setValue([this.selectedValue]);
      }

    } catch (error) {
      if (this.isStaleSearch(seq)) return;
      console.error('ForeignKeyEditor: Exception during fetch', error);
      this.choicesInstance.setChoices([], 'value', 'label', true); // Clear choices on error
    }
  }

  getGui() {
    return this.eGui;
  }

  afterGuiAttached(params) {
    // Set initial display value
    if (this.selectedValue) {
      this.initialChoiceObject = this.getInitialChoice(this.selectedValue.value, this.params) ;//this.getInitialChoiceObject(this.selectedValue, this.params.data, this.params.colDef);
      if (this.initialChoiceObject) {
        this.choicesInstance.setChoices([this.initialChoiceObject], 'value', 'label', false); // false to append/not clear
      }
    }

    // Trigger an initial fetch for dropdown options (e.g. with empty search term)
    // This populates the dropdown when it's first opened, before user types.
    this.performSearch(''); 

    this.choicesInstance.showDropdown();
    this.choicesInstance.input.element.focus();

    // If AG Grid provides a charPress, use it to pre-fill and filter
    if (params && params.charPress) {
      this.choicesInstance.input.element.value = params.charPress;
      // Dispatch input event to trigger our debouncedSearch
      this.choicesInstance.input.element.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  getValue() {
    // The chosen reference's display values are published to the shared cache rather than
    // written onto params.data: a grid refresh can swap the row's data object out from
    // under an open editor, so anything written here may land on an orphan.
    const fkColId = this.params.colDef.colId;
    // Until a selection is made, selectedValue still holds the initial {value, label}
    // choice object rather than a bare value.
    const rawValue = (this.selectedValue && typeof this.selectedValue === 'object')
      ? this.selectedValue.value
      : this.selectedValue;
    const cleared = rawValue === '' || rawValue === null || rawValue === undefined;

    if (!cleared && this.selectedDisplayName !== undefined) {
      rememberFkDisplay(fkColId, rawValue, {
        display_name: this.selectedDisplayName,
        key_parts: this.selectedKeyParts || []
      });
    }

    // Return null for empty/clear selection so the backend stores NULL.
    // Otherwise this is the referenced record's trdm_record_id; the server resolves the
    // matching trdm_sk from the current version at write time.
    //
    // Choices.js hands back a string, but the grid holds the record id as the number the
    // server sent. Returning a number keeps the grid's change detection comparing like
    // with like, so re-picking the value a row already has is correctly a no-op.
    if (cleared) return null;
    const asNumber = Number(rawValue);
    return Number.isFinite(asNumber) ? asNumber : rawValue;
  }

  // For inline editor, this is called when editing is to be stopped.
  // Return true if you want to cancel the editing.
  isCancelBeforeStart() {
    return false;
  }

  // For inline editor, return true if you want to cancel editing after it started.
  isCancelAfterEnd() {
    // Example: prevent closing if the input is invalid (not applicable here yet)
    return false;
  }

  isPopup() {
    return true; // Crucial for inline editing
  }

  destroy() {
    this.destroyed = true;
    if (this.choicesInstance) {
      this.choicesInstance.destroy();
      this.choicesInstance = null;
    }
  }
}
